import { LIVE_TICK_MS, formatTimeOnSet } from '@mixwire/contracts';
import { useRef, useState, type ReactElement, type ReactNode } from 'react';

import { readApiFailure } from '../api/client.js';
import { ColumnCorner } from '../components/ColumnCorner.js';
import { ErrorBanner } from '../components/ErrorBanner.js';
import { IdentityChip } from '../components/IdentityChip.js';
import { LoadingStatus } from '../components/LoadingStatus.js';
import { Logo } from '../components/Logo.js';
import { ModalShell } from '../components/ModalShell.js';
import { NowPlayingBlock as SharedNowPlayingBlock } from '../components/NowPlayingBlock.js';
import { PartyEndedModal } from '../components/PartyEndedModal.js';
import { PartyQrCode } from '../components/PartyQrCode.js';
import { pillButton } from '../components/pillButton.js';
import { RateLimitLine } from '../components/RateLimitNotice.js';
import { SyncMeter } from '../components/SyncMeter.js';
import { TrackRow } from '../components/TrackRow.js';
import { useAuthStatus } from '../hooks/useAuth.js';
import { useIdleDismiss } from '../hooks/useIdleDismiss.js';
import { useIdleReturnToTop } from '../hooks/useIdleReturnToTop.js';
import { useLiveProgress } from '../hooks/useLiveProgress.js';
import { useNow } from '../hooks/useNow.js';
import { usePageTitle } from '../hooks/usePageTitle.js';
import { usePartyView } from '../hooks/usePartyView.js';
import { useQueuedSongsVisibility } from '../hooks/useQueuedSongsVisibility.js';
import { useVerifyPasskey } from '../hooks/useVerifyPasskey.js';
import { colourName } from '../lib/channelColour.js';
import { endedByLine, wasJustEndedByMe } from '../lib/endedParty.js';
import { withoutNowPlaying } from '../lib/playedHistory.js';
import { upNextInPlayOrder } from '../lib/playOrder.js';
import { formatStartsIn } from '../lib/time.js';
import { partyPath, suggestPath } from '../routing/paths.js';
import { suggesterTokenKey } from '../routing/suggesterToken.js';
import { useRoute } from '../routing/useRoute.js';

import type { PartySlug, PublicChannel, QueuedTrack } from '@mixwire/contracts';

const HIDDEN_HOTSPOT_TAPS = 3;
const HIDDEN_HOTSPOT_WINDOW_MS = 1200;
const MS_PER_SECOND = 1000;

/**
 * A passcode prompt left open on a projector is a passcode on a projector, so
 * an untouched one shows itself out: fading from 20s, gone by 30s.
 */
const IDLE_DISMISS_AFTER_MS = 30_000;

const NowPlayingBlock = ({ slot }: { readonly slot: PublicChannel }): ReactElement | null => {
  const { nowPlaying } = slot;
  // Fills in the seconds between polls so the screen a projector's worth of
  // people are watching reads as a clock, not a stopwatch that jumps.
  const elapsedMs = useLiveProgress(nowPlaying, nowPlaying?.playing === true);

  if (nowPlaying === null) {
    return (
      <p className="text-muted mt-3 text-sm">
        {slot.channel?.connected === true ? 'Nothing playing right now.' : 'Not connected.'}
      </p>
    );
  }

  const position = Math.min(
    nowPlaying.durationSeconds,
    nowPlaying.positionSeconds + Math.floor(elapsedMs / MS_PER_SECOND),
  );

  return (
    <div className="mt-3">
      <SharedNowPlayingBlock
        title={nowPlaying.title}
        artist={nowPlaying.artist}
        colour={slot.colour}
        size={64}
        imageUrl={nowPlaying.imageUrl}
        positionSeconds={position}
        durationSeconds={nowPlaying.durationSeconds}
        playing={nowPlaying.playing}
      />
    </div>
  );
};

const TrackList = ({
  heading,
  children,
}: {
  readonly heading: string;
  readonly children: ReactNode;
}): ReactElement => (
  <div className="mt-4">
    <p className="text-muted text-[10px] tracking-widest uppercase">{heading}</p>
    <ul className="mt-1.5 flex flex-col gap-1">{children}</ul>
  </div>
);

const NextRow = ({
  track,
  nowMs,
}: {
  readonly track: QueuedTrack;
  readonly nowMs: number;
}): ReactElement => (
  <li className="text-secondary flex justify-between gap-2 text-xs">
    <span className="min-w-0 truncate">{track.title}</span>
    <span className="text-muted shrink-0">{formatStartsIn(track.startsAt, nowMs)}</span>
  </li>
);

const ChannelLane = ({
  slot,
  nowMs,
  areQueuedSongsShown,
}: {
  readonly slot: PublicChannel;
  readonly nowMs: number;
  readonly areQueuedSongsShown: boolean;
}): ReactElement => {
  const recentlyPlayed = withoutNowPlaying(slot.played, slot.nowPlaying);
  return (
    <div className="bg-surface rounded-card border-subtle min-w-0 overflow-hidden border">
      <div className="h-1.5" style={{ backgroundColor: slot.colour }} />
      <div className="min-w-0 p-4">
        <p className="text-xs font-medium tracking-widest uppercase" style={{ color: slot.colour }}>
          {colourName(slot.colour)}
        </p>

        {slot.channel === null ? (
          <p className="text-muted mt-2 text-sm">No DJ connected</p>
        ) : (
          <>
            <div className="mt-1 flex items-baseline justify-between gap-2">
              <p className="text-content min-w-0 truncate text-lg font-semibold">
                {slot.channel.displayName}
              </p>
              {/* Deliberately tiny: on set for an hour is worth knowing, never
                worth reading before what is playing. */}
              <span
                title="Time on set at this party"
                className="text-muted shrink-0 text-[10px] tabular-nums"
              >
                {formatTimeOnSet(slot.channel, nowMs)}
              </span>
            </div>

            <NowPlayingBlock slot={slot} />

            <RateLimitLine rateLimit={slot.rateLimit} />

            {!areQueuedSongsShown || slot.next.length === 0 ? null : (
              <TrackList heading="Then">
                {slot.next.slice(0, 2).map((track) => (
                  <NextRow key={track.id} track={track} nowMs={nowMs} />
                ))}
              </TrackList>
            )}

            {recentlyPlayed.length === 0 ? null : (
              <TrackList heading="Recently played">
                {recentlyPlayed.slice(0, 2).map((track) => (
                  <li key={track.id} className="text-muted truncate text-xs tabular-nums">
                    {track.playedAt} {track.title}
                  </li>
                ))}
              </TrackList>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const UpNextStrip = ({
  channels,
  nowMs,
}: {
  readonly channels: readonly PublicChannel[];
  readonly nowMs: number;
}): ReactElement | null => {
  const items = upNextInPlayOrder(channels);

  if (items.length === 0) {
    return null;
  }

  return (
    <div>
      <p className="text-muted text-xs tracking-widest uppercase">Coming up · in play order</p>
      {/* Below `md` this stacks vertically: a horizontal strip on a phone just grows off the edge instead of scrolling by touch. */}
      <div className="mt-2.5 grid grid-cols-1 gap-2.5 md:flex md:overflow-x-auto md:pb-1">
        {items.map((track) => (
          <div
            key={track.id}
            className="bg-surface rounded-control flex items-center gap-2.5 border border-l-[3px] py-2 pr-3.5 pl-2.5 md:shrink-0"
            style={{ borderColor: 'var(--ui-border-subtle)', borderLeftColor: track.colour }}
          >
            <TrackRow
              title={track.title}
              artist={track.artist}
              meta={formatStartsIn(track.startsAt, nowMs)}
              colour={track.colour}
              size={36}
              imageUrl={track.imageUrl}
              stackClassName="min-w-0 flex-1 md:max-w-40 md:flex-none"
            />
          </div>
        ))}
      </div>
    </div>
  );
};

const QueuedSongsToggle = ({
  areQueuedSongsShown,
  onToggle,
}: {
  readonly areQueuedSongsShown: boolean;
  readonly onToggle: () => void;
}): ReactElement => (
  <button type="button" onClick={onToggle} className={pillButton('quiet', 'sm')}>
    {areQueuedSongsShown ? 'Hide queued songs' : 'Show queued songs'}
  </button>
);

type BecomeSuggesterModalProps = {
  readonly slug: PartySlug;
  readonly onClose: () => void;
};

const BecomeSuggesterModal = ({ slug, onClose }: BecomeSuggesterModalProps): ReactElement => {
  const [passkey, setPasskey] = useState('');
  const verify = useVerifyPasskey();
  const { navigate } = useRoute();
  useIdleDismiss({ dismissAfterMs: IDLE_DISMISS_AFTER_MS, onDismiss: onClose });

  const failure = readApiFailure(verify.error);

  const submit = (): void => {
    verify.mutate(
      { slug, passkey },
      {
        onSuccess: (response) => {
          sessionStorage.setItem(suggesterTokenKey(slug), response.token);
          navigate(suggestPath(slug));
        },
      },
    );
  };

  return (
    <ModalShell title="Become a suggester" eyebrow="Passcode" onDismiss={onClose}>
      <p className="text-secondary mt-2 text-sm leading-relaxed">
        Two words from any DJ in the room. This device gets to suggest tracks — it never gets DJ
        controls.
      </p>

      <form
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        <input
          value={passkey}
          onChange={(event) => {
            setPasskey(event.target.value);
          }}
          placeholder="PLACE-HOLDER"
          aria-label="Passcode"
          className="border-line text-content placeholder:text-muted focus:outline-accent-ring mt-5 w-full rounded-control border px-4 py-3 text-center font-mono text-base tracking-widest uppercase focus:outline-2 focus:outline-offset-2"
        />

        {failure === undefined ? null : (
          <p role="alert" className="text-danger mt-2.5 text-sm">
            {failure.message}
          </p>
        )}

        <div className="mt-5 flex items-center gap-2">
          <button
            type="submit"
            disabled={verify.isPending || passkey.trim() === ''}
            className={`${pillButton('accent')} flex-1`}
          >
            {verify.isPending ? 'Checking…' : 'Become a suggester'}
          </button>
          <button type="button" onClick={onClose} className={pillButton('ghost')}>
            Cancel
          </button>
        </div>
      </form>
    </ModalShell>
  );
};

type PartyViewPageProps = {
  readonly slug: PartySlug;
  /** Arriving by the short `/<party>/add` link is the same as asking for the prompt. */
  readonly opensWithPasscodePrompt?: boolean;
};

// Every field comes from the server's shared party cache, so the projector
// can't drift from the decks, and costs Spotify nothing since nobody here is
// signed in.
export const PartyViewPage = ({
  slug,
  opensWithPasscodePrompt = false,
}: PartyViewPageProps): ReactElement => {
  const partyView = usePartyView(slug);
  const nowMs = useNow(LIVE_TICK_MS);
  useIdleReturnToTop();
  const auth = useAuthStatus();
  const [isModalOpen, setIsModalOpen] = useState(opensWithPasscodePrompt);
  const { areQueuedSongsShown, toggleQueuedSongs } = useQueuedSongsVisibility();
  // The DJ who pressed End arrives having just done it, so their arrival counts
  // as the acknowledgement and they go straight to the greyed-out view.
  const [hasSeenEnding, setHasSeenEnding] = useState(() => wasJustEndedByMe(slug));
  const draftTapTimes = useRef<number[]>([]);

  const party = partyView.data;
  const isEnded = (party?.endedAt ?? null) !== null;
  // A signed-in DJ is never offered the suggester flow: their way around the
  // party is the identity chip, which is already on this page.
  const isSignedInDj = auth.data?.authenticated === true;

  usePageTitle(party?.displayName);

  const onWordmarkTap = (): void => {
    if (isSignedInDj) {
      return;
    }

    const now = Date.now();
    const recent = [...draftTapTimes.current, now].filter(
      (time) => now - time <= HIDDEN_HOTSPOT_WINDOW_MS,
    );
    draftTapTimes.current = recent;
    if (recent.length >= HIDDEN_HOTSPOT_TAPS) {
      draftTapTimes.current = [];
      setIsModalOpen(true);
    }
  };

  return (
    <div className="flex min-w-0 flex-col gap-6">
      <div className="flex items-center justify-between gap-4">
        <button
          type="button"
          onClick={onWordmarkTap}
          className="font-display text-content -m-2 flex shrink-0 items-center gap-2 rounded-control p-2 text-2xl select-none"
        >
          <Logo className="size-7" />
          MixWire
        </button>
        <div className="flex min-w-0 items-center gap-2.5">
          <span className="text-secondary truncate text-sm">{party?.displayName}</span>
          <SyncMeter />
          <IdentityChip />
        </div>
      </div>

      <ColumnCorner edge="bottom">
        <PartyQrCode path={partyPath(slug)} className="shadow-raised" />
      </ColumnCorner>

      {partyView.isPending ? <LoadingStatus>Loading the party…</LoadingStatus> : null}

      {partyView.isError ? <ErrorBanner>Could not find that party.</ErrorBanner> : null}

      {party === undefined ? null : (
        <div className="relative">
          <div className={isEnded ? 'pointer-events-none opacity-40 grayscale' : undefined}>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              {party.channels.map((slot) => (
                <ChannelLane
                  key={slot.id}
                  slot={slot}
                  nowMs={nowMs}
                  areQueuedSongsShown={areQueuedSongsShown}
                />
              ))}
            </div>

            <div className="mt-6">
              <UpNextStrip channels={areQueuedSongsShown ? party.channels : []} nowMs={nowMs} />
            </div>
          </div>

          {isEnded && hasSeenEnding ? (
            <div
              role="status"
              className="absolute inset-0 flex items-start justify-center p-4 text-center"
            >
              <div className="bg-surface border-subtle shadow-raised rounded-card mt-10 max-w-sm border p-6">
                <p className="font-display text-content text-2xl">This party has ended</p>
                <p className="text-secondary mt-2 text-sm">
                  {endedByLine(party.endedBy)} If that is unexpected, your DJs will likely start a
                  new one shortly.
                </p>
              </div>
            </div>
          ) : null}
        </div>
      )}

      <div className="flex flex-col items-center gap-2">
        <p className="text-muted text-center text-xs">
          View only. Ask a DJ for the passcode to suggest a track.
        </p>
        {party === undefined || isEnded ? null : (
          <QueuedSongsToggle
            areQueuedSongsShown={areQueuedSongsShown}
            onToggle={toggleQueuedSongs}
          />
        )}
      </div>

      {isEnded && party !== undefined && !hasSeenEnding ? (
        <PartyEndedModal
          partyDisplayName={party.displayName}
          endedBy={party.endedBy}
          onAcknowledge={() => {
            setHasSeenEnding(true);
          }}
        />
      ) : null}

      {isModalOpen && !isSignedInDj ? (
        <BecomeSuggesterModal
          slug={slug}
          onClose={() => {
            setIsModalOpen(false);
          }}
        />
      ) : null}
    </div>
  );
};
